import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Badge, Spinner, Alert, Button,
    InputGroup, Form, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function StockCritique() {
    const navigate = useNavigate();
    const [produits, setProduits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterType, setFilterType] = useState('tous');
    const [sortConfig, setSortConfig] = useState({ key: 'urgence', direction: 'desc' });

    useEffect(() => {
        fetchStockCritique();
    }, []);

    const fetchStockCritique = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stock_etat.php?action=critique',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduits(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getNiveauAlerte = (niveau) => {
        if (niveau === 'rupture') {
            return (
                <Badge 
                    className="px-3 py-2 rounded-pill"
                    style={{
                        background: 'linear-gradient(135deg, #dc3545, #bb2d3b)',
                        border: 'none',
                        boxShadow: '0 2px 4px rgba(220, 53, 69, 0.3)'
                    }}
                >
                    <i className="ri-close-circle-line me-1"></i>
                    Rupture
                </Badge>
            );
        }
        return (
            <Badge 
                className="px-3 py-2 rounded-pill"
                style={{
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    border: 'none',
                    boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
                }}
            >
                <i className="ri-alert-line me-1"></i>
                Critique
            </Badge>
        );
    };

    const getUrgenceLevel = (produit) => {
        if (produit.niveau_alerte === 'rupture') return 3;
        if (produit.quantite_reelle <= produit.seuil_alerte / 2) return 2;
        return 1;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const filteredProduits = produits
        .filter(p => {
            const matchesSearch = p.reference?.toLowerCase().includes(filterText.toLowerCase()) ||
                p.designation?.toLowerCase().includes(filterText.toLowerCase());
            
            if (filterType === 'rupture') return p.niveau_alerte === 'rupture' && matchesSearch;
            if (filterType === 'critique') return p.niveau_alerte === 'critique' && matchesSearch;
            return matchesSearch;
        })
        .sort((a, b) => {
            if (sortConfig.key === 'urgence') {
                const niveauA = getUrgenceLevel(a);
                const niveauB = getUrgenceLevel(b);
                return sortConfig.direction === 'asc' ? niveauA - niveauB : niveauB - niveauA;
            }
            if (sortConfig.key === 'quantite_reelle') {
                return sortConfig.direction === 'asc' 
                    ? (a.quantite_reelle || 0) - (b.quantite_reelle || 0)
                    : (b.quantite_reelle || 0) - (a.quantite_reelle || 0);
            }
            if (sortConfig.key === 'designation') {
                return sortConfig.direction === 'asc'
                    ? a.designation?.localeCompare(b.designation)
                    : b.designation?.localeCompare(a.designation);
            }
            return 0;
        });

    const totalRupture = produits.filter(p => p.niveau_alerte === 'rupture').length;
    const totalCritique = produits.filter(p => p.niveau_alerte === 'critique').length;
    const valeurTotaleRupture = produits
        .filter(p => p.niveau_alerte === 'rupture')
        .reduce((sum, p) => sum + (p.quantite_reelle * p.prix_achat_ht || 0), 0);

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Analyse des stocks critiques...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchStockCritique}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.15))',
                                            boxShadow: '0 10px 20px rgba(245, 158, 11, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-alert-line text-warning" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Stock critique
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            {produits.length} produit(s) nécessitent une attention
                                        </p>
                                    </div>
                                </div>
                                <Button variant="outline-secondary" onClick={() => navigate('/stock/etat/instantane')}>
                                    <i className="ri-arrow-left-line me-2"></i>Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Cartes d'alerte */}
                    <Row className="g-4 mb-4 stats-row">
    {/* Rupture de stock */}
    <Col md={4}>
        <Card className="border-0 shadow-sm h-100 stat-card stat-card-danger">
            <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                    <div className="stat-icon-wrapper bg-danger-subtle me-3">
                        <i className="ri-close-circle-line text-danger fs-3"></i>
                    </div>
                    <div className="stat-content">
                        <span className="stat-label text-muted text-uppercase small fw-semibold tracking-wide">Rupture de stock</span>
                        <div className="d-flex align-items-baseline">
                            <h2 className="stat-value fw-bold mb-0 me-2">{totalRupture}</h2>
                            <span className="stat-badge bg-danger-subtle text-danger small px-2 py-1 rounded">épuisé</span>
                        </div>
                        <small className="text-muted">produits en rupture</small>
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>

    {/* Stock critique */}
    <Col md={4}>
        <Card className="border-0 shadow-sm h-100 stat-card stat-card-warning">
            <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                    <div className="stat-icon-wrapper bg-warning-subtle me-3">
                        <i className="ri-alert-line text-warning fs-3"></i>
                    </div>
                    <div className="stat-content">
                        <span className="stat-label text-muted text-uppercase small fw-semibold tracking-wide">Stock critique</span>
                        <div className="d-flex align-items-baseline">
                            <h2 className="stat-value fw-bold mb-0 me-2">{totalCritique}</h2>
                            <span className="stat-badge bg-warning-subtle text-warning small px-2 py-1 rounded">alerte</span>
                        </div>
                        <small className="text-muted">sous le seuil d'alerte</small>
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>

    {/* Valeur en rupture */}
    <Col md={4}>
        <Card className="border-0 shadow-sm h-100 stat-card">
            <Card.Body className="p-4">
                <div className="d-flex align-items-center">
                    <div className="stat-icon-wrapper bg-secondary-subtle me-3">
                        <i className="ri-money-euro-circle-line text-secondary fs-3"></i>
                    </div>
                    <div className="stat-content">
                        <span className="stat-label text-muted text-uppercase small fw-semibold tracking-wide">Valeur en rupture</span>
                        <h3 className="stat-value fw-bold text-dark mb-1">{formatCurrency(valeurTotaleRupture)}</h3>
                        <small className="text-muted">capital immobilisé</small>
                        <div className="stat-progress mt-2">
                            <div className="progress" style={{ height: '3px' }}>
                                <div className="progress-bar bg-secondary" style={{ width: '45%' }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
</Row>

                    {/* Filtres */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={5}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <i className="ri-search-line text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher par référence, produit..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="border-start-0"
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="tous">🔔 Toutes les alertes</option>
                                        <option value="rupture">❌ Rupture</option>
                                        <option value="critique">⚠️ Critique</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Select 
                                        value={sortConfig.key}
                                        onChange={(e) => setSortConfig({ key: e.target.value, direction: 'desc' })}
                                        className="filter-select"
                                    >
                                        <option value="urgence">Trier par urgence</option>
                                        <option value="quantite_reelle">Trier par stock</option>
                                        <option value="designation">Trier par nom</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Button variant="outline-secondary" onClick={() => {
                                        setFilterText('');
                                        setFilterType('tous');
                                    }} className="w-100">
                                        <i className="ri-refresh-line me-2"></i>Réinitialiser
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des produits critiques */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Réf.</th>
                                            <SortableHeader field="designation">Produit</SortableHeader>
                                            <th>Catégorie</th>
                                            <SortableHeader field="quantite_reelle" className="text-center">Stock actuel</SortableHeader>
                                            <th className="text-center">Seuil</th>
                                            <th className="text-center">Manquant</th>
                                            <th>Valeur</th>
                                            <th>Niveau</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProduits.length > 0 ? (
                                            filteredProduits.map((p, index) => {
                                                const ratio = (p.quantite_reelle / p.seuil_alerte) * 100;
                                                const urgenceLevel = getUrgenceLevel(p);
                                                return (
                                                    <tr key={p.id_produit} className="critique-row" style={{
                                                        animation: `fadeIn 0.3s ease ${index * 0.03}s both`,
                                                        backgroundColor: urgenceLevel === 3 ? 'rgba(220, 53, 69, 0.05)' : 
                                                                       urgenceLevel === 2 ? 'rgba(245, 158, 11, 0.05)' : 'transparent'
                                                    }}>
                                                        <td>
                                                            <Badge bg="light" text="dark" className="px-3 py-2 font-mono">
                                                                {p.reference}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Link to={`/stock/produits/${p.id_produit}`} className="text-primary fw-semibold text-decoration-none">
                                                                {p.designation}
                                                            </Link>
                                                        </td>
                                                        <td>{p.nom_categorie || <span className="text-muted">-</span>}</td>
                                                        <td className="text-center" style={{ minWidth: '140px' }}>
                                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={<Tooltip>Niveau: {Math.min(ratio, 100).toFixed(0)}% du seuil</Tooltip>}
                                                                >
                                                                    <ProgressBar 
                                                                        now={Math.min(ratio, 100)} 
                                                                        variant={p.niveau_alerte === 'rupture' ? 'danger' : 'warning'}
                                                                        style={{ width: '80px', height: '8px', borderRadius: '4px' }}
                                                                    />
                                                                </OverlayTrigger>
                                                                <span className={`fw-bold ${p.niveau_alerte === 'rupture' ? 'text-danger' : ''}`}>
                                                                    {p.quantite_reelle}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge 
                                                                bg="secondary" 
                                                                className="px-3 py-2"
                                                                style={{ background: '#6c757d' }}
                                                            >
                                                                {p.seuil_alerte}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            {p.quantite_a_commander > 0 ? (
                                                                <span className="fw-bold text-danger">
                                                                    <i className="ri-arrow-down-line me-1"></i>
                                                                    {p.quantite_a_commander}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td className="fw-bold text-primary">
                                                            {formatCurrency(p.quantite_reelle * p.prix_achat_ht)}
                                                        </td>
                                                        <td>{getNiveauAlerte(p.niveau_alerte)}</td>
                                                        <td className="text-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Réapprovisionner</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    className="rounded-pill px-3 d-inline-flex align-items-center gap-2"
                                                                    onClick={() => navigate(`/stock/mouvements/ajouter/${p.id_produit}?type=entree`)}
                                                                >
                                                                    <i className="ri-add-line"></i>
                                                                    <span>Commander</span>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
                                                            <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '3rem' }}></i>
                                                        </div>
                                                        <h5 className="mt-3">Aucun produit en stock critique</h5>
                                                        <p className="text-muted mb-3">
                                                            {filterText || filterType !== 'tous' 
                                                                ? "Aucun résultat ne correspond à vos filtres"
                                                                : "Tous les stocks sont à un niveau satisfaisant"}
                                                        </p>
                                                        {(filterText || filterType !== 'tous') && (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => {
                                                                    setFilterText('');
                                                                    setFilterType('tous');
                                                                }}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>Réinitialiser
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Actions recommandées */}
                    {filteredProduits.length > 0 && (
                        <Card className="border-0 shadow-sm mt-4 bg-light">
                            <Card.Body className="p-4">
                                <h6 className="mb-3">
                                    <i className="ri-lightbulb-line me-2 text-warning"></i>
                                    Actions recommandées
                                </h6>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <div className="d-flex align-items-center p-3 bg-white rounded-3">
                                            <div className="bg-danger bg-opacity-10 rounded-circle p-3 me-3">
                                                <i className="ri-shopping-cart-line text-danger fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Commandes urgentes</small>
                                                <strong className="fs-5">{totalRupture + totalCritique}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="d-flex align-items-center p-3 bg-white rounded-3">
                                            <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                                                <i className="ri-truck-line text-warning fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Montant à commander</small>
                                                <strong className="fs-5">{formatCurrency(valeurTotaleRupture)}</strong>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="d-flex align-items-center p-3 bg-white rounded-3">
                                            <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                                <i className="ri-file-copy-line text-primary fs-4"></i>
                                            </div>
                                            <div>
                                                <small className="text-muted d-block">Commandes groupées</small>
                                                <strong className="fs-5">Créer</strong>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .stats-row .stat-card {
                        transition: all 0.3s ease;
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .stats-row .stat-card:hover {
                        transform: translateY(-5px);
                        box-shadow: 0 20px 25px -5px rgba(0,0,0,0.2) !important;
                    }

                    .filter-card {
                        border-radius: 16px;
                        background: #f8fafc;
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    .critique-row {
                        transition: all 0.2s ease;
                    }

                    .sortable-header:hover {
                        color: #4361ee;
                    }

                    .search-input .form-control:focus,
                    .filter-select:focus {
                        box-shadow: none;
                        border-color: #4361ee;
                    }

                    .font-mono {
                        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
                    }

                    .empty-state {
                        padding: 2rem;
                    }

                    .progress {
                        background-color: #e9ecef;
                    }


                    .stats-row .stat-card {
    border-radius: 16px;
    transition: all 0.2s ease;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.03);
}

.stats-row .stat-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.1) !important;
    border-color: rgba(0, 0, 0, 0.05);
}

.stat-icon-wrapper {
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    transition: all 0.2s ease;
}

.stat-card:hover .stat-icon-wrapper {
    transform: scale(1.05);
}

.bg-danger-subtle {
    background-color: rgba(220, 53, 69, 0.08);
}

.bg-warning-subtle {
    background-color: rgba(255, 193, 7, 0.08);
}

.bg-secondary-subtle {
    background-color: rgba(108, 117, 125, 0.08);
}

.stat-label {
    letter-spacing: 0.4px;
    font-size: 0.7rem;
    color: #6c757d !important;
}

.stat-value {
    line-height: 1.2;
}

.stat-badge {
    font-size: 0.7rem;
    font-weight: 500;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
}

.stat-progress {
    opacity: 0.5;
    transition: opacity 0.2s ease;
}

.stat-card:hover .stat-progress {
    opacity: 1;
}

.progress {
    background-color: #e9ecef;
    border-radius: 10px;
    overflow: hidden;
}

.progress-bar {
    border-radius: 10px;
    transition: width 0.3s ease;
}

.tracking-wide {
    letter-spacing: 0.5px;
}
                `}</style>

                <Footer />
            </div>
        </>
    );
}